/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-18 18:26:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';

import WidgetRow from './widget-row';
import ConfirmDeleteModal from 'src/components/views/recommendation/confirm-delete-modal';
import ConfirmActionModal from 'src/components/views/recommendation/predictive-bundle/confirm-action-modal';
import { COLUMN_PREDICTIVE_BUNDLE } from 'src/constants/bundle';

import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import * as oauthAction from 'src/stores/actions/oauth-actions';

import { ComposedProps } from './index.type';

const ListWidgets = (props: ComposedProps) => {
  const [activeRemoveModal, setActiveRemoveModal] = useState(false);
  const [activeActionModal, setActiveActionModal] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [selectedWidgetName, setSelectedWidgetName] = useState('');
  const [actionType, setActionType] = useState('');

  const {
    i18n,
    saving = false,
    saveSuccess = false,
    deleting = false,
    deleteSuccess = false,
    allWidgets = [],
    updateWidget,
    handleDeleteWidget
  } = props;

  const handleWidgetAction = ({ widgetId, widgetName }, type) => {
    setSelectedId(widgetId);
    setSelectedWidgetName(widgetName);
    switch (type) {
      case 'delete': {
        setActiveRemoveModal(true);
        break;
      }
      case 'active':
      case 'inactive':
      default: {
        setActionType(type);
        setActiveActionModal(true);
      }
    }
  };

  // Delete widget
  const deleteWidget = () => {
    handleDeleteWidget(selectedId);
  };

  const onUpdateStatusWidget = () => {
    const widget = allWidgets.find(element => element.id === selectedId);
    updateWidget(selectedId, {
      recommendationType: widget.recommendationType,
      bundleStatus: actionType
    });
  };

  // Close action modal when update status success
  useEffect(() => {
    if (!saving && saveSuccess) {
      setActiveActionModal(false);
    }
  }, [saving, saveSuccess]);

  // Close remove modal when remove widget success
  useEffect(() => {
    if (!deleting && deleteSuccess) {
      setActiveRemoveModal(false);
    }
  }, [deleting, deleteSuccess]);

  return (
    <div className="Polaris-Card__Section list-widgets">
      <table className="Polaris-DataTable__Table">
        <thead>
          <tr>
            {_.map(COLUMN_PREDICTIVE_BUNDLE, (column, index) => (
              <td
                style={{
                  width: `${column.width}%`,
                  textAlign: `${column.textAlign}`
                }}
                key={index}
                className={`Polaris-DataTable__Cell Polaris-DataTable__Cell--header`}>
                <div style={{ fontWeight: '600' }} data-tut={column.value}>
                  {i18n.translate(`AdminHTML.PredictiveBundle.Management.${column.label}`)}
                </div>
              </td>
            ))}
          </tr>
        </thead>
        <tbody className={`Polaris-DataTable__Table recommendation-collapse `}>
          <>
            {_.map(allWidgets, (item, index) => (
              <WidgetRow key={index} widget={item} handleWidgetAction={handleWidgetAction} />
            ))}
          </>
        </tbody>
      </table>

      <ConfirmDeleteModal
        active={activeRemoveModal}
        onToggleModal={() => setActiveRemoveModal(false)}
        onDelete={deleteWidget}
        deleting={deleting}
        title={i18n.translate('AdminHTML.PredictiveBundle.DeleteModal.title')}
        content={<div>{i18n.translate('AdminHTML.PredictiveBundle.DeleteModal.content')}</div>}
      />
      <ConfirmActionModal
        active={activeActionModal}
        onToggleModal={() => setActiveActionModal(false)}
        widgetId={selectedId}
        actionType={actionType}
        widgetName={selectedWidgetName}
        onAction={onUpdateStatusWidget}
        loading={saving}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  markDoneTourWidget: meta =>
    dispatch(recommendationAction.markDoneTourWidget(meta, ownProps.i18n)),
  updateFeaturesAppSettings: (data, featureName) =>
    dispatch(oauthAction.updateFeaturesAppSettings(data, featureName))
});

const mapStateToProps = (state, props) => {
  return {
    saving: _.get(state.bundle, 'saving', false),
    saveSuccess: _.get(state.bundle, 'saveSuccess', false),
    deleting: _.get(state.bundle, 'deleting', false),
    deleteSuccess: _.get(state.bundle, 'deleteSuccess', false),
    success: _.get(state.bundle, 'success', false)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ListWidgets));
